import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';
import Button from '@prisjakt/ui-button';
import CrossIcon from 'mdi-react/CloseIcon';
import PanelHeader from '@prisjakt/ui-panel-header';
import debounce from 'lodash/debounce';

import { createExperimentHook } from '@prisjakt/ui-experiment';
import ErrorBoundary from '../../components/error-boundary';
import PageContent from '../../components/page-content';
import NoScroll from '../../components/no-scroll';

import {
  campaignElasticSearchReceive,
  campaignQuery,
  campaignPopularQuery,
  campaignCategoryFilter,
  campaignsRangeFilter,
} from './actions';

import Deals from './components/deals';
import CampaignHeader from './components/campaign-header';
import CampaignInactive from './components/campaign-inactive';
import Search from './components/search';
import Popular from './components/popular';
import Categories from './components/categories';
import Stores, { mapStores } from './components/stores';
import FilterRanges from './components/filters/ranges';
import Banner from './components/banner';
import TagCloud from './components/tag-cloud';
import CampaignBreadcrumb from './components/breadcrumb';
import Aggregations from './components/aggregations';
import CampaignArticles from './components/campaign-articles';

import { DAILY_DEALS_ID } from '../../globals/helpers/campaign/campaign-ids';

import * as S from './styles';

const rangeOptions = [
  {
    key: 'price',
    field: 'price.offer',
    translation: 'filter.price',
    showBars: false,
  },
  {
    key: 'productRating',
    field: 'product.user_reviews.rating',
    translation: 'filter.product-rating',
    showBars: true,
  },
  {
    key: 'storeRating',
    field: 'store.user_reviews.rating',
    translation: 'filter.store-rating',
    showBars: true,
  },
];

const DEFAULT_VISIBLE_STORES = 5;

const buildRouter = location => {
  const qs = queryString.decode(location.search.substr(1));

  const router = {
    qs,
    offset: parseInt(qs.offset, 10) || 0,
    query: qs.query ? qs.query : '',
    sort: qs.sort,
    filterRanges: {},
    categories: qs.category ? qs.category.split('|') : [],
    stores: qs.stores ? qs.stores.split('|') : [],
    maxStores: parseInt(qs.max_stores, 10) || DEFAULT_VISIBLE_STORES,
  };

  rangeOptions.forEach(option => {
    router.filterRanges[option.key] = '';
    if (qs[option.key]) {
      const range = qs[option.key].split('-');
      if (range.length === 2) {
        router.filterRanges[option.key] = {
          from: parseFloat(range[0], 10),
          to: parseFloat(range[1], 10),
        };
      }
    }
  });

  return router;
};

const adsViewUpdateAction = view => ({
  type: 'ADS_VIEW_UPDATE',
  view: `campaigns:${view || ''}`,
});

const experimentCategories = {
  gb: [
    1760,
    1042,
    1180,
    819,
    1024,
    890,
    892,
    560,
    1217,
    1022,
    581,
    326,
    1182,
    335,
    743,
    539,
    537,
    500,
    1790,
    524,
    967,
    97,
    523,
    1068,
    1807,
    101,
    542,
    549,
    576,
    360,
    1602,
    1010,
    879,
    812,
    1001,
    531,
    532,
    530,
    401,
    712,
    791,
    350,
    547,
    546,
    1029,
    544,
    1270,
    148,
    820,
    580,
    2,
    1737,
    1520,
    1793,
    1852,
    358,
    516,
    1427,
    570,
    380,
    1739,
    353,
    1071,
    1183,
    379,
    534,
    1543,
    103,
    1003,
    393,
    1320,
    433,
    998,
    1922,
    965,
    1672,
    1699,
    465,
    1827,
    1707,
    776,
    1428,
    1887,
    974,
    1808,
    893,
    559,
    941,
    894,
    40,
    1594,
    551,
    1497,
    514,
    107,
    515,
    84,
    513,
    766,
    1713,
  ],
  it: [
    103,
    107,
    1497,
    1428,
    148,
    1594,
    401,
    353,
    101,
    1707,
    513,
    350,
    532,
    1808,
    514,
    523,
    515,
    879,
    393,
    576,
    812,
    581,
    326,
    1827,
    433,
    580,
    820,
    1427,
    1520,
    500,
    1046,
    889,
    1922,
    84,
    2,
    1320,
    97,
    893,
    766,
    892,
    712,
    553,
    965,
    335,
    315,
    1790,
    1713,
    534,
    559,
    1807,
  ],
  no: [
    148,
    107,
    1827,
    295,
    965,
    743,
    1003,
    544,
    1001,
    1029,
    549,
    974,
    1027,
    967,
    546,
    542,
    1147,
    1138,
    1010,
    1022,
    1602,
    547,
    990,
    1144,
    1229,
    1224,
    1239,
    2009,
    1139,
    966,
    992,
    101,
    84,
    326,
    97,
    353,
    393,
    1594,
    350,
    380,
    386,
    379,
    893,
    335,
    500,
    352,
    1320,
    727,
    433,
    1818,
    360,
    1793,
    1790,
    1375,
    358,
    436,
    1427,
    1428,
    1497,
    1526,
    819,
    820,
    1509,
    894,
    766,
    941,
    1543,
    401,
    1707,
    712,
    1922,
    1217,
    1713,
    515,
    520,
    513,
    559,
    576,
    555,
    560,
    1068,
    611,
    532,
    1701,
    1326,
    1354,
    523,
    1077,
    553,
    1737,
    514,
    1838,
    1106,
    530,
    581,
    1070,
    1836,
    1974,
    1107,
    1153,
    556,
    1209,
    539,
    1046,
    571,
    580,
    1912,
    1238,
    1834,
    570,
    534,
    1152,
    1918,
    997,
    1155,
    1126,
    1198,
    1722,
    1328,
    537,
    1327,
    1079,
    767,
    1637,
    569,
    531,
    1381,
    1597,
    1087,
    1110,
    1679,
    1924,
    516,
    1936,
    1562,
    568,
    528,
    1975,
    1915,
    1134,
    527,
    1067,
    1193,
    1182,
    1180,
    1183,
    1807,
    1887,
    1863,
    1171,
    1869,
    1683,
    103,
    879,
    1808,
    1331,
    897,
    1332,
    830,
    834,
    1357,
    840,
    902,
    1283,
    889,
    909,
    910,
    836,
    795,
    917,
    1940,
    890,
    803,
    839,
    1419,
    867,
    817,
    924,
    837,
    815,
  ],
  se: [
    401,
    1707,
    1217,
    712,
    439,
    1922,
    1713,
    1976,
    897,
    889,
    834,
    830,
    836,
    1331,
    867,
    890,
    840,
    917,
    1332,
    1283,
    887,
    795,
    817,
    1357,
    1940,
    902,
    923,
    1756,
    801,
    924,
    803,
    916,
    2103,
    1183,
    1180,
    1182,
    1807,
    1887,
    1874,
    1869,
    1683,
    1809,
    1171,
    1871,
    1863,
    1971,
    2029,
    101,
    326,
    84,
    97,
    1113,
    103,
    148,
    107,
    1497,
    353,
    1427,
    1428,
    393,
    1827,
    965,
    401,
    1707,
    1594,
    1808,
    879,
    350,
    766,
    386,
    1217,
    819,
    515,
    101,
    1922,
    520,
    1183,
    1672,
    379,
    380,
    553,
    439,
    893,
    295,
    2,
    889,
    500,
    1180,
    712,
    820,
    544,
    526,
    1713,
    513,
    326,
    1320,
    743,
    1737,
    1399,
    555,
    1182,
    335,
    1760,
    352,
    1068,
    523,
    1003,
    1526,
    1029,
    84,
    1807,
    894,
    1354,
    576,
    549,
    974,
    1740,
    1887,
    560,
    890,
    532,
    1543,
    1153,
    941,
    25,
    1818,
    1326,
    1209,
    1790,
    97,
    1530,
    559,
    727,
    360,
    1001,
    603,
    433,
    1145,
    1792,
    1701,
    1520,
    1147,
    1619,
    1110,
    534,
    24,
    1750,
    1976,
    1739,
    40,
    1046,
    582,
    1834,
    546,
    37,
    663,
    1071,
    1106,
    530,
    91,
    1074,
    539,
    1637,
    1912,
    967,
    1346,
    537,
    358,
    836,
    473,
    527,
    570,
    1838,
    514,
    1113,
    1610,
    542,
    1070,
    28,
    1509,
    436,
    1602,
    1568,
    1836,
    645,
    1010,
    106,
    902,
    1711,
    713,
    897,
    990,
    1477,
    580,
    812,
    1528,
    2112,
    1107,
    1679,
    1723,
    551,
    584,
    556,
    775,
    568,
    776,
    834,
    1793,
    581,
    1722,
    398,
    168,
    601,
    1870,
    1239,
    516,
    1078,
    830,
    1639,
    1845,
    1126,
    455,
    370,
    1869,
    1027,
    363,
    917,
    606,
    1238,
    1978,
    892,
    47,
    465,
    795,
    1138,
    1381,
    2103,
    1874,
    369,
    1726,
    1382,
    817,
    658,
    1283,
    1328,
    391,
    1022,
    1918,
    887,
    1970,
    531,
  ],
};

const useCampaignFilterExperiment = createExperimentHook(
  'campaign_filter',
  {},
  {
    percentage: {
      range: {
        'price.diff_percentage': {
          gte: 25,
        },
      },
    },
    categories: {
      experimentCategories,
    },
    popularity: {
      range: {
        clicks: {
          gte: 1,
        },
      },
    },
  }
);

const CampaignDealsExperiment = ({ children }) => {
  const { market } = useContext(AppContext);

  const filter = useCampaignFilterExperiment();

  if (filter.experimentCategories) {
    return children({
      terms: {
        'product.category.path.id':
          filter.experimentCategories[market.code] || [],
      },
    });
  }

  return children(filter);
};

class Campaign extends React.PureComponent {
  constructor(props) {
    super(props);

    const router = buildRouter(props.location);
    this.state = {
      showFilter: false,
      expanded: [],
      router,
      stores: [],
    };

    this.afterUpdateParams = debounce((url, routerParams) => {
      // Only replace url if user is still on the page

      this.props.history.replace(url);
      this.props.campaignQuery(routerParams);
    }, 1500);
  }

  componentDidMount() {
    this.unlisten = this.props.history.listen(location => {
      const qs = queryString.decode(location.search.substr(1));
      const offset = parseInt(qs.offset || '0', 10);

      // HACK: Offset is not triggering new update, so we need to trigger it manually
      if (this.state.router.offset !== offset) {
        this.updateUrlParams({
          key: 'offset',
          value: offset,
          deleteOffset: false,
        });
      }
    });

    // Send initial query form url
    this.props.campaignQuery(this.state.router);

    // Explicitly tell ads to update visibility/load (needed when lazy loading pages)
    this.props.adsViewUpdateAction('mounted');
  }

  componentWillUnmount() {
    this.afterUpdateParams.cancel();
    this.unlisten();
  }

  findTreeNode(tree, value, parents) {
    if (!tree) {
      return false;
    }

    // Check all direct children
    let foundNode = tree.find(node => node.id === value);
    if (foundNode) {
      foundNode.parents = parents;
      return foundNode;
    }

    // Do recursive search if it wasn't found in the direct children
    tree.some(node => {
      if (node.children && node.children.length > 0) {
        // Add the current node id to the parents
        foundNode = this.findTreeNode(node.children, value, [
          ...parents,
          node.id,
        ]);
        if (foundNode) {
          return true;
        }
      }
      return false;
    });

    // Return result, will contain the node if found, otherwise false
    return foundNode;
  }

  adjustGroupedUrlParams(currentValues, newValue, keep) {
    let values = currentValues.split('|');
    const index = values.indexOf(newValue);

    // Exclude the value if it is found in the current key, add it otherwise
    if (index !== -1 && !keep) {
      values.splice(index, 1);
    } else {
      values.push(newValue);
    }

    // Only unique values
    values = [...new Set(values)];

    // Check if the new value was added
    if (values.includes(newValue)) {
      const node = this.findTreeNode(
        this.props.data.categoryTree,
        newValue,
        []
      );

      // Deselect any found parents
      if (node && node.parents && node.parents.length > 0) {
        values = values.filter(value => !node.parents.includes(value));
      }

      // Deselct any found children
      if (node && node.children && node.children.length > 0) {
        if (values && values.length > 0) {
          values = values.filter(value => {
            return (
              value === newValue || !this.findTreeNode(node.children, value, [])
            );
          });
        }
      }
    }

    return values.join('|');
  }

  updateUrlParams(item) {
    const { key, value, isGrouped, keep, deleteOffset = true } = item;
    const qs = this.state.router.qs;

    let newValue = value;
    if (qs[key] && isGrouped) {
      newValue = this.adjustGroupedUrlParams(qs[key], newValue, keep);
    } else if (qs[key] && newValue === qs[key] && !keep) {
      // The value was found in the query string and is equal to the given value, delete it
      newValue = false;
      delete qs[key];
    }

    // Remove all filter ranges if the query is changed
    if (key === 'query') {
      Object.keys(this.state.router.filterRanges).forEach(filterKey => {
        delete qs[filterKey];
      });

      // Sort on relevance if the query string isn't empty
      qs.sort = newValue ? 'relevance' : 'product.popularity.total';
    }

    let newParams = qs;
    if (newValue) {
      newParams = { ...newParams, [key]: newValue };
    } else {
      // Remove the key if no value exists
      delete qs[key];
    }

    if (deleteOffset) {
      // Remove any potential offset if the url params are updated
      delete newParams.offset;
    }

    const search =
      Object.keys(newParams).length > 0
        ? `?${queryString.encode(newParams)}`
        : '';

    this.executeSearch(search);
  }

  executeSearch(search) {
    const { location } = this.props.history;
    const url = location.pathname + search;
    const routerParams = buildRouter({
      pathname: location.pathname,
      search,
    });

    this.setState({ router: routerParams }, () =>
      this.afterUpdateParams(url, routerParams)
    );
  }

  clearFilter(keepSort) {
    this.executeSearch(
      keepSort ? `?${queryString.encode({ sort: this.state.router.sort })}` : ''
    );
  }

  getSelectedFilters() {
    const selectedFilters = [];
    const router = this.state.router;
    // Add the filtered categories
    router.categories.forEach(id => {
      const node = this.findTreeNode(this.props.data.categoryTree, id, []);
      if (node) {
        selectedFilters.push({
          id: node.id,
          name: node.name,
          type: 'category',
          isGrouped: true,
        });
      }
    });

    router.stores.forEach(id => {
      const store = this.state.stores.filter(x => x.id === id);
      if (store.length > 0) {
        selectedFilters.push({
          id: store[0].id,
          name: store[0].name,
          type: 'stores',
          isGrouped: true,
        });
      }
    });

    // Add the filter ranges
    rangeOptions.forEach(option => {
      const name = `${this.context.t('campaigns', option.translation)}: ${
        router.filterRanges[option.key].from
      } - ${router.filterRanges[option.key].to}`;
      if (router.filterRanges[option.key]) {
        selectedFilters.push({
          id: `${router.filterRanges[option.key].from}-${
            router.filterRanges[option.key].to
          }`,
          name,
          type: option.key,
          isGrouped: false,
        });
      }
    });

    return selectedFilters;
  }

  render() {
    const router = this.state.router;
    const selectedFilters = this.getSelectedFilters();
    const selectionsCount = selectedFilters.length;
    const { campaign, categoryTree, campaigns, page } = this.props.data;
    const { articles } = campaign;
    const featuredArticle = articles
      ? articles.find(item => item.featured)
      : null;

    const activeCampaign = campaign.state === 'ACTIVE';
    const activeCampaigns = campaigns.filter(
      item => item.id !== campaign.id && item.state === 'ACTIVE'
    );
    const dailyDeals = activeCampaigns.find(c => c.id === DAILY_DEALS_ID);

    if (!activeCampaign) {
      return (
        <PageContent withAds>
          <S.Campaign>
            {this.context.renderMode !== 'native' && (
              <CampaignBreadcrumb campaign={campaign} />
            )}

            <CampaignHeader
              isActive={false}
              campaign={campaign}
              onClear={() => this.clearFilter()}
            />

            <CampaignInactive
              targetDate={parseInt(campaign.countdown || campaign.start, 10)}
              campaign={campaign}
              page={page}
              dailyDeals={dailyDeals}
              router={router}
            />

            <S.CampaignGrouping
              title={this.context.t('campaigns', 'themes.ongoing-campaigns')}
              campaigns={activeCampaigns}
            />
          </S.Campaign>
        </PageContent>
      );
    }

    return (
      <PageContent withAds>
        <S.Campaign>
          {this.context.renderMode !== 'native' && (
            <CampaignBreadcrumb campaign={campaign} />
          )}

          <CampaignHeader
            isActive
            campaign={campaign}
            onClear={() => this.clearFilter()}
          />
          <CampaignDealsExperiment>
            {experimentFilter => (
              <S.GutterWrapper>
                <S.ProductWrapper>
                  <ErrorBoundary onError={() => null}>
                    <S.FiltersWrapper show={this.state.showFilter}>
                      <S.Filters show={this.state.showFilter}>
                        <S.Filter>
                          <S.FilterContent>
                            <NoScroll
                              mobile
                              tablet
                              enabled={this.state.showFilter}
                            />

                            <PanelHeader
                              divider="none"
                              title={<T domain="campaigns">filter.title</T>}
                            />

                            <S.Search hideMobile>
                              <Search
                                query={router.query}
                                campaignName={campaign.name}
                                onSearch={item =>
                                  this.updateUrlParams({
                                    key: item.id,
                                    value: item.value,
                                  })
                                }
                              />
                            </S.Search>

                            <S.Tags hideMobile>
                              {selectionsCount > 0 && (
                                <TagCloud
                                  selectionsCount={selectionsCount}
                                  selected={selectedFilters}
                                  onChange={item =>
                                    this.updateUrlParams({
                                      key: item.id,
                                      value: item.value,
                                      isGrouped: item.isGrouped,
                                    })
                                  }
                                  onClear={() => this.clearFilter(true)}
                                />
                              )}
                            </S.Tags>

                            <S.FiltersPopular>
                              <h6>
                                <T domain="campaigns" campaign={campaign.name}>
                                  popular-filters.title
                                </T>
                              </h6>
                              <Popular
                                filterPaths={
                                  this.props.popularCampaignFilterPaths
                                }
                                location={this.props.history.location}
                                onChange={location => {
                                  const routerParams = buildRouter(location);

                                  this.props.campaignPopularQuery(
                                    routerParams,
                                    this.props.popularCampaignFilterPaths.find(
                                      filter => filter.path === location.search
                                    )
                                  );

                                  this.setState({
                                    showFilter: false,
                                  });
                                  this.executeSearch(location.search);
                                }}
                              />
                            </S.FiltersPopular>
                            <Aggregations
                              campaignId={campaign.id}
                              router={router}
                              categoryTree={categoryTree}
                              expandedCategories={this.state.expanded}
                              experimentFilter={experimentFilter}
                              onSuccess={data => {
                                this.setState({
                                  stores: mapStores(data.aggregations),
                                });
                              }}
                              render={({ data }) => {
                                const stores =
                                  data && mapStores(data.aggregations);

                                return (
                                  <div>
                                    <h6>
                                      <T domain="campaigns">category.title</T>
                                    </h6>
                                    <Categories
                                      campaignId={campaign.id}
                                      aggregations={
                                        data &&
                                        data.aggregations.category.current
                                          .buckets
                                      }
                                      onChange={(item, category) => {
                                        this.updateUrlParams({
                                          key: 'category',
                                          value: item.value,
                                          isGrouped: true,
                                        });
                                        this.props.campaignCategoryFilter(
                                          category
                                        );
                                      }}
                                      categoryTree={categoryTree}
                                      selected={router.categories}
                                      expanded={this.state.expanded}
                                      onExpand={(e, id) => {
                                        e.preventDefault();
                                        this.setState(prevState => ({
                                          expanded: prevState.expanded.includes(
                                            id
                                          )
                                            ? prevState.expanded.filter(
                                                item => item !== id
                                              )
                                            : prevState.expanded.concat([id]),
                                        }));
                                      }}
                                    />
                                    <S.FilterRanges>
                                      <FilterRanges
                                        campaignId={campaign.id}
                                        data={data}
                                        router={this.state.router}
                                        rangeOptions={rangeOptions}
                                        selectionsCount={selectionsCount}
                                        onSuccess={
                                          this.props
                                            .campaignElasticSearchReceive
                                        }
                                        onChange={item => {
                                          this.updateUrlParams({
                                            key: item.id,
                                            value: item.value,
                                            keep: item.keep,
                                          });
                                          this.props.campaignsRangeFilter(item);
                                        }}
                                      />
                                    </S.FilterRanges>
                                    <S.PromoBanner>
                                      <Banner
                                        banners={campaign.banners}
                                        type="header_promo_banner"
                                      />
                                    </S.PromoBanner>
                                    <h6>
                                      <T domain="campaigns">store.title</T>
                                    </h6>
                                    <Stores
                                      campaignId={campaign.id}
                                      stores={stores}
                                      hasMore={Boolean(
                                        data &&
                                          data.aggregations.stores.current
                                            .sum_other_doc_count > 0
                                      )}
                                      showingMore={Boolean(
                                        stores &&
                                          stores.length > DEFAULT_VISIBLE_STORES
                                      )}
                                      onChange={item => {
                                        this.updateUrlParams({
                                          key: 'stores',
                                          value: item.value,
                                          isGrouped: true,
                                        });
                                      }}
                                      onMoreChanged={showMore => {
                                        if (showMore) {
                                          this.updateUrlParams({
                                            key: 'max_stores',
                                            value: 100,
                                          });
                                        } else {
                                          this.updateUrlParams({
                                            key: 'max_stores',
                                            value: DEFAULT_VISIBLE_STORES,
                                          });
                                        }
                                      }}
                                      selected={router.stores}
                                    />
                                  </div>
                                );
                              }}
                            />
                          </S.FilterContent>
                        </S.Filter>

                        <S.FiltersHeader>
                          <h4>
                            <T domain="campaigns">filter.title</T>
                            <button
                              type="button"
                              onClick={() =>
                                this.setState({
                                  showFilter: false,
                                })
                              }
                            >
                              <CrossIcon />
                            </button>
                          </h4>
                          <S.FiltersButton>
                            <Button
                              color="primary"
                              onClick={() =>
                                this.setState({
                                  showFilter: false,
                                })
                              }
                              variant={
                                selectionsCount === 0 ? 'outlined' : 'default'
                              }
                            >
                              <T domain="campaigns">filter.apply-button</T>
                            </Button>
                          </S.FiltersButton>
                        </S.FiltersHeader>
                      </S.Filters>
                    </S.FiltersWrapper>
                  </ErrorBoundary>

                  <S.Products>
                    <ErrorBoundary onError={() => null}>
                      <S.Tags hideDesktop>
                        <S.FilterShowButton>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              this.setState({ showFilter: true });
                            }}
                            size="small"
                          >
                            <T domain="campaigns">filter.show-button</T>
                          </Button>
                        </S.FilterShowButton>
                        {selectionsCount > 0 && (
                          <TagCloud
                            selectionsCount={selectionsCount}
                            selected={selectedFilters}
                            onChange={item =>
                              this.updateUrlParams({
                                key: item.id,
                                value: item.value,
                                isGrouped: item.isGrouped,
                              })
                            }
                            onClear={() => this.clearFilter(true)}
                          />
                        )}
                      </S.Tags>
                    </ErrorBoundary>

                    <S.Deals>
                      <Deals
                        campaignId={campaign.id}
                        campaignName={campaign.name}
                        router={router}
                        rangeOptions={rangeOptions}
                        selectionsCount={selectionsCount}
                        experimentFilter={experimentFilter}
                        onCampaignItemClick={() => {
                          this.afterUpdateParams.flush();
                        }}
                        onChange={item =>
                          this.updateUrlParams({
                            key: item.id,
                            value: item.value,
                          })
                        }
                      />
                    </S.Deals>
                  </S.Products>
                  <S.PromoBanner>
                    <Banner
                      banners={campaign.banners}
                      type="header_promo_banner"
                    />
                  </S.PromoBanner>
                </S.ProductWrapper>
              </S.GutterWrapper>
            )}
          </CampaignDealsExperiment>

          {!campaign.facetted.isFacetted && (
            <ErrorBoundary onError={() => null}>
              <CampaignArticles
                featuredArticle={featuredArticle}
                articles={articles}
                campaignName={campaign.name}
              />
            </ErrorBoundary>
          )}
        </S.Campaign>
      </PageContent>
    );
  }
}

const { string, func, number, shape, arrayOf, object } = PropTypes;

Campaign.propTypes = {
  base: string.isRequired,
  data: shape({
    campaigns: arrayOf(
      shape({
        slug: string,
        title: shape({
          short: string,
          long: string,
        }),
        description: shape({
          short: string,
          long: string,
        }),
        descriptionHtml: shape({
          short: string,
          long: string,
          page: string,
        }),
        banners: arrayOf(
          shape({
            url: string,
            type: string,
            imageUrl: string,
          })
        ),
        articles: arrayOf(
          shape({
            id: number,
            publishedAt: string,
            title: string,
            preamble: string,
            imageUrl: string,
            imageCaption: string,
            pathName: string,
          })
        ),
      })
    ).isRequired,
    categoryTree: arrayOf(
      shape({
        id: string.isRequired,
        name: string.isRequired,
        children: arrayOf(object),
      })
    ),
  }),
  popularCampaignFilterPaths: arrayOf(object).isRequired,
  history: shape({
    replace: func,
  }).isRequired,
  location: shape({
    search: string,
    pathname: string,
  }).isRequired,
  campaignElasticSearchReceive: func.isRequired,
  campaignQuery: func.isRequired,
  campaignPopularQuery: func.isRequired,
  campaignCategoryFilter: func.isRequired,
  campaignsRangeFilter: func.isRequired,
  adsViewUpdateAction: func.isRequired,
};

Campaign.defaultProps = {
  data: { categoryTree: [] },
};

Campaign.contextType = AppContext;

export default connect(
  state => ({
    base: state.areas.campaign.base,
    popularCampaignFilterPaths: state.areas.campaign.popularCampaignFilterPaths,
  }),
  {
    campaignElasticSearchReceive,
    campaignQuery,
    campaignPopularQuery,
    campaignCategoryFilter,
    campaignsRangeFilter,
    adsViewUpdateAction,
  }
)(withRouter(Campaign));
